import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Dialog, DialogContent, IconButton, Paper, Stack, Typography, } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import LoginIcon from '@mui/icons-material/Login';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getWvBrandingSafe } from '../../services/wvBrandingConfig';
import { resolvePublicUrl } from '../../utils/publicUrl';
function optionMeta(opt) {
    const p = (opt.accessPortal ?? '').toUpperCase();
    if (p === 'E') {
        return {
            Icon: PersonOutlineIcon,
            hint: 'For citizens, businesses, and licensed professionals using the public portal.',
        };
    }
    if (p === 'I') {
        return {
            Icon: AdminPanelSettingsOutlinedIcon,
            hint: 'For state employees and authorized internal users.',
        };
    }
    return {
        Icon: LoginIcon,
        hint: 'Continue to the secure sign-in page.',
    };
}
/**
 * Branded customer vs staff login chooser (matches WV OneStop palette + typography).
 */
const LoginPortalDialog = ({ open, onClose, options, embedLoginUrl, onEmbedBack, onSelectOption, }) => {
    const theme = useTheme();
    const sealPath = getWvBrandingSafe().wvStateSealPng;
    const seal = resolvePublicUrl(sealPath) ?? sealPath;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, scroll: "body", PaperProps: {
            elevation: 8,
            sx: {
                borderRadius: 3,
                overflow: 'hidden',
                maxWidth: 460,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                boxShadow: `0 24px 48px ${alpha(theme.palette.primary.dark, 0.18)}`,
            },
        }, children: [_jsxs(Box, { sx: {
                    position: 'relative',
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(theme.palette.primary.light, 0.95)} 100%)`,
                    color: '#fff',
                    px: 3,
                    pt: 2.75,
                    pb: 2.25,
                }, children: [_jsx(IconButton, { "aria-label": "Close", onClick: onClose, size: "small", sx: {
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'rgba(255,255,255,0.9)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                        }, children: _jsx(CloseIcon, { fontSize: "small" }) }), embedLoginUrl ? (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { pr: 4 }, children: [_jsx(IconButton, { "aria-label": "Back", onClick: onEmbedBack, size: "small", sx: { color: '#fff', mr: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }, children: _jsx(ArrowBackIcon, {}) }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                                            fontFamily: theme.typography.h5.fontFamily,
                                            fontWeight: 600,
                                            fontSize: '1.2rem',
                                            lineHeight: 1.3,
                                        }, children: "Sign in" }), _jsx(Typography, { sx: { fontSize: '0.8rem', opacity: 0.88, mt: 0.25, fontFamily: theme.typography.body2.fontFamily }, children: "Secure authentication" })] })] })) : (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 2, sx: { pr: 4 }, children: [_jsx(Box, { component: "img", src: seal, alt: "", sx: {
                                    width: 52,
                                    height: 52,
                                    objectFit: 'contain',
                                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                                } }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                                            fontFamily: theme.typography.h5.fontFamily,
                                            fontWeight: 600,
                                            fontSize: '1.25rem',
                                            letterSpacing: 0.2,
                                            lineHeight: 1.25,
                                        }, children: "West Virginia OneStop" }), _jsx(Typography, { sx: {
                                            fontFamily: theme.typography.body2.fontFamily,
                                            fontSize: '0.875rem',
                                            opacity: 0.9,
                                            mt: 0.35,
                                            fontWeight: 400,
                                        }, children: "Choose how you\u2019d like to sign in" })] })] }))] }), _jsx(DialogContent, { sx: { p: 2.5, pt: embedLoginUrl ? 2 : 2.5, pb: 3, bgcolor: theme.palette.grey[50] }, children: embedLoginUrl ? (_jsx(Box, { component: "iframe", title: "Sign in", src: embedLoginUrl, sx: {
                        width: '100%',
                        minHeight: 440,
                        border: 'none',
                        borderRadius: 2,
                        bgcolor: '#fff',
                        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.divider, 0.9)}`,
                    } })) : (_jsx(Stack, { spacing: 2, children: options.map((opt) => {
                        const { Icon, hint } = optionMeta(opt);
                        return (_jsx(Paper, { elevation: 0, sx: {
                                borderRadius: 2.5,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                borderLeftWidth: 4,
                                borderLeftColor: theme.palette.secondary.main,
                                bgcolor: '#fff',
                                transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                                overflow: 'hidden',
                                '&:hover': {
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    transform: 'translateY(-2px)',
                                    borderColor: alpha(theme.palette.secondary.main, 0.45),
                                },
                            }, children: _jsxs(Box, { component: "button", type: "button", onClick: () => onSelectOption(opt.url), sx: {
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    p: 2.25,
                                    textAlign: 'left',
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                    font: 'inherit',
                                    color: 'inherit',
                                }, children: [_jsx(Box, { sx: {
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            color: theme.palette.primary.main,
                                        }, children: _jsx(Icon, { sx: { fontSize: 26 } }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                                    fontFamily: theme.typography.h6.fontFamily,
                                                    fontWeight: 600,
                                                    fontSize: '1.05rem',
                                                    color: theme.palette.primary.main,
                                                    lineHeight: 1.3,
                                                    mb: 0.5,
                                                }, children: opt.label }), _jsx(Typography, { variant: "body2", sx: {
                                                    fontFamily: theme.typography.body2.fontFamily,
                                                    color: 'text.secondary',
                                                    lineHeight: 1.5,
                                                    fontSize: '0.8125rem',
                                                }, children: hint })] }), _jsx(ArrowForwardIosIcon, { sx: {
                                            fontSize: 14,
                                            color: theme.palette.secondary.main,
                                            mt: 1,
                                            flexShrink: 0,
                                            opacity: 0.85,
                                        } })] }) }, opt.label + opt.url));
                    }) })) })] }));
};
export default LoginPortalDialog;
